import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-lg font-normal ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90 h-14 px-8 shadow-glow",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 h-14 px-8",
        outline:
          "border-2 border-border bg-transparent hover:bg-muted h-14 px-8",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/90 h-14 px-8",
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-foreground underline-offset-4 hover:underline",
        success: "bg-success text-success-foreground hover:bg-success/90 h-14 px-8",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 h-14 px-8",
      },
      size: {
        default: "h-14 px-8",
        sm: "h-10 px-4 text-base",
        lg: "h-16 px-12 text-xl",
        icon: "h-14 w-14",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
